import {Component, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';

import {
  faChevronDown,
  faChevronRight,
  faChevronUp,
  faCircleInfo,
  faCircleSmall,
  faCube,
  faMemoCircleCheck
} from "@fortawesome/pro-solid-svg-icons";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {StepperComponent} from '../../util/design/stepper/stepper.component';
import {CdkStep} from "@angular/cdk/stepper";
import {BicButtonComponent, TooltipDirective} from "@buildinginformationcloud/bic-ui-components";
import {AllInCheckNavigationService} from "../../../services/checks/all-in-check-navigation.service";
import {FilePrefetchService} from "../../../services/prefetch/file-prefetch.service";
import {QualityRulesPrefetchService} from "../../../services/prefetch/quality-rules-prefetch.service";
import {PermissionService} from "../../../services/permission/permission.service";

@Component({
  selector: 'all-in-check',
  imports: [
    StepperComponent,
    FaIconComponent,

    CdkStep,
    BicButtonComponent,
    TooltipDirective
  ],
  templateUrl: './all-in-check.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './all-in-check.component.scss'
})
export class AllInCheckComponent implements OnInit {
  private filePrefetchService = inject(FilePrefetchService);
  protected navigation = inject(AllInCheckNavigationService);
  private qualityRulesPrefetchService = inject(QualityRulesPrefetchService);

  protected permissionService = inject(PermissionService);

  ngOnInit(): void {
    // HOAI und LOIplus laufen über myChecks; nur der All-Right Check nutzt noch die alten
    // Regel-Prefetches. Die Dateien braucht die myCheck-Auswahl ebenfalls.
    this.qualityRulesPrefetchService.prefetch();
    this.filePrefetchService.prefetch();
  }

  readonly loiInfos: String[] = [
    "Nutzung des vordefinierten BIC-Standards in insgesamt fünf Detailstufen",
    "Import vorhandener Attributlisten über Excel-Vorlage",
    "Benutzerdefinierte Regelerstellung zur Definition eigener Modellierungsstandards",
    "Bauteilbasierte Auswertung der Attributierung mit direkter Anbindung im Modell"
  ]

  readonly bimBaseInfos = ['Komponenten müssen einen Namen haben', 'Eindeutige GUIDs', 'Geschossbezogene Modellierung', 'Sinnvolle Bodenflächen', 'Sinnvolle Raumflächen',
    'Model muss Komponenten enthalten', 'Jeder Raum muss eine Tür enthalten', 'Vorhandensein von Treppen in mehrstöckigen Gebäuden', 'Mehrschichtige Modellierung',
    'Korrekte Klassifizierung', 'Türen in Wänden modelliert', 'Fenster in Wänden modelliert'];
  readonly bimBaseTooltips = ['Das Attribut IfcName muss ausgefüllt sein', 'Jede GUID (Global Unique Identifier) darf maximal einmal im Modell vorkommen',
    'Jede Elementkomponente (IfcElement) und jede Raumkomponente (IfcSpace) müssen einem Geschoss zugeordnet sein', 'Die Geschosskomponente (IfcBuildingStory) muss größer als 0 m² sein',
    'Jede Raumkomponente (IfcSpace) muss größer als 0 m² sein', 'Jede Wand muss mindestens 1 cm dick, 5 cm hoch und 5 cm lang sein', 'Die Modellkomponenten IfcSite, IfcBuilding, IfcWall, IfcSlab, IfcSpace müssen im Modell vorhanden sein',
    'In jeder Raumkomponente (IfcSpace) im Modell muss mindestens eine Türkomponente (IfcDoor) erhalten sein', 'In jedem Gebäude (IfcBuilding), welches mehr als ein Geschoss hat, muss mindestens eine Treppenkomponente (IfcStair) vorhanden sein',
    'Überprüfung, ob Elemente mehrschichtig modelliert sind (Wände, Decken, Dächer)', 'Überprüft, ob Komponenten richtig klassifiziert sind, z.b. Wand als IfcWall', 'Jede Türkomponente (IfcDoor) muss einer Wandkomponente (IfcWall) zugeordnet sein',
    'Jede Fensterkomponente (IfcWindow) muss einer Wandkomponente (IfcWall) zugeordnet sein']

  readonly hoaiBulletPoints1: String[] = [
    "Auswahl der zu prüfenden Grund- und besonderen Leistungen nach Bedarf",
    "Systematische Erfassung und Klassifizierung von Plänen, Textdokumenten und Berechnungen",
    "KI-basierte Überprüfung der inhaltlichen Vollständigkeit analog HOAI Teilleistungen mittels Textanalytik"
  ]

  readonly hoaiBulletPoints2: String[] = [
    "Unterstützung gängiger Dateiformate: PDF, DOCX, PPTX, XLSX",
    "Detailliertes Feedback zu Dokumenten und Teilleistungen",
    "Strukturierter, interaktiver Report für alle Leistungsphasen"
  ]

  readonly bimBulletPoints1: String[] = [
    "Modul zur allgemeinen und individuellen Prüfung von IFC-Modellen",
    "Allgemeine Qualitätssicherung digitaler Gebäudemodelle",
    "Abgleich der Modellinformationen mit vordefinierten oder projektspezifischen Standards (LOIN)"
  ]

  readonly bimBulletPoints2: String[] = [
    "Intuitive Visualisierung von BIM-/IFC-Modellen direkt auf der Plattform",
    "Detaillierte, bauteilbasierte Anzeige aller BIM-Daten und IFC-Eigenschaften",
    "Schnelle und einfache Tasks-Erstellung (Issue-Management) im BCF-Format"
  ]

  expandedHoaiInfo1: boolean = false;
  expandedHoaiInfo2: boolean = false;
  expandedBimInfo1:boolean = false;
  expandedBimInfo2:boolean = false;

  /** Einstiege liegen im {@link AllInCheckNavigationService} – dieselben nutzen Banner und Dashboard. */
  toHoai() {
    // Der frühere Gate PROJECT_HOAI_CHECK ist ein Legacy-Privileg, das keine aktuelle Rolle mehr
    // vergibt (wie PROJECT_BIM_TOOLS_CHECK unten) -- der Einstieg hängt am Ausführungsrecht.
    if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) return;
    this.navigation.startHoaiCheck();
  }

  toLoi() {
    // PROJECT_BIM_TOOLS_CHECK ist ein Legacy-Privileg, das keine aktuelle Rolle mehr vergibt
    // (siehe Privilege-Enum im Backend) -- der Einstieg haengt am Ausfuehrungsrecht.
    if (!this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE')) return;
    this.navigation.startLoiplusCheck();
  }

  protected readonly faCircleSmall = faCircleSmall;
  protected readonly faChevronRight = faChevronRight;
  protected readonly faChevronDown = faChevronDown;
  protected readonly faMemoCircleCheck = faMemoCircleCheck;
  protected readonly faChevronUp = faChevronUp;
  protected readonly faCube = faCube;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly top = top;
}
